/**
 * The Orders packet on paper (Matt's V2 as Abner updated it on Oct 18, fixed
 * pages): every laid-out page of `layoutPacket`, and the off-screen render
 * the measure host reads unit heights from. Both render the same `PacketPage`
 * frame and `UnitView`s at the same width, so what was measured is what
 * prints. The pages sit on the packet's own named print page (quarter-inch
 * margins, `globals.css`). A page whose counts can still change carries the
 * INCOMPLETE ORDER label on its band and the watermark over it; the others
 * print clean (`sectionLocksAtLabel`, PROD-1452).
 */

import { useMemo } from 'react'

import {
  ORDERS_PACKET_PAGE_CLASS,
  PacketPage,
  PacketRenderContext,
  type PacketRenderContextValue,
} from '@/components/chefs/print/packet/chrome'
import { UnitView } from '@/components/chefs/print/packet/unit-view'
import { PrintPreliminaryLabel, PrintPreliminaryPageMark } from '@/components/chefs/print/print-preliminary-watermark'
import { PrintHeaderNoticeContext } from '@/components/print/print-header'
import { usePrintedAt } from '@/hooks/chefs/usePrintedAt'
import { type LaidOutPage, type PacketLayout, pageRangeLabel } from '@/lib/chefs/packet/layout'
import { sectionLocksAtLabel } from '@/lib/chefs/packet/preliminary'
import type { PacketModel, PacketSection, PacketUnit } from '@/lib/chefs/packet/units'
import { printedAtLabel } from '@/lib/chefs/print-identity'
import type { PrintPreliminary } from '@/lib/chefs/print-plan'

/** "Page 4 of 28 · Prep Recipes": the footer prints the page on the right and the section's name on the left. */
export function packetPageLabel(page: Pick<LaidOutPage, 'number' | 'footer'>, totalPages: number): string {
  const group =
    page.footer.index !== null && page.footer.count !== null
      ? `${page.footer.label} ${page.footer.index} of ${page.footer.count}`
      : page.footer.label
  return `Page ${page.number} of ${totalPages} · ${group}`
}

function unitIndex(model: PacketModel): Map<string, PacketUnit> {
  const units = new Map<string, PacketUnit>()
  for (const section of model.sections) {
    for (const unit of [...section.units, ...section.repeat]) {
      units.set(unit.id, unit)
    }
  }
  return units
}

export interface PacketDocumentProps {
  model: PacketModel
  layout: PacketLayout
  /** The lock labels per page scope while a school can still change its order (PROD-1452); null = final. */
  preliminary?: PrintPreliminary | null
}

/** The packet, page by page. */
export function PacketDocument({ model, layout, preliminary = null }: PacketDocumentProps) {
  const printedAt = printedAtLabel(usePrintedAt())
  const sections = useMemo(() => new Map(model.sections.map((section) => [section.id, section])), [model])
  const units = useMemo(() => unitIndex(model), [model])
  const context = useMemo<PacketRenderContextValue>(
    () => ({ pageOf: (ids) => pageRangeLabel(layout.ranges, ids), totalPages: layout.totalPages }),
    [layout],
  )
  return (
    <PacketRenderContext.Provider value={context}>
      <div className={ORDERS_PACKET_PAGE_CLASS}>
        {layout.pages.map((page, i) => {
          const section = sections.get(page.sectionId)
          if (!section) {
            return null
          }
          const locksAtLabel = sectionLocksAtLabel(section, preliminary)
          return (
            <PrintHeaderNoticeContext.Provider
              key={`${page.sectionId}|${page.index}`}
              value={locksAtLabel ? <PrintPreliminaryLabel locksAtLabel={locksAtLabel} /> : null}>
              <PacketPage
                mode="print"
                band={section.band}
                title={section.title}
                index={page.index}
                count={page.count}
                pageLabel={packetPageLabel(page, layout.totalPages)}
                footerLabel={page.footer.label}
                printedAt={printedAt}
                pageBreak={i < layout.pages.length - 1}
                mark={locksAtLabel ? <PrintPreliminaryPageMark locksAtLabel={locksAtLabel} /> : null}>
                {page.items.map((item) => {
                  const unit = units.get(item.unitId)
                  return unit ? (
                    <div key={item.unitId} data-packet-unit-id={item.unitId}>
                      <UnitView
                        unit={unit}
                        continued={section.kind !== 'shopping-list' && item.continued}
                        compact={page.compact}
                      />
                    </div>
                  ) : null
                })}
              </PacketPage>
            </PrintHeaderNoticeContext.Provider>
          )
        })}
      </div>
    </PacketRenderContext.Provider>
  )
}

/** Placeholders the size of real page numbers, for measuring. */
const MEASURE_CONTEXT: PacketRenderContextValue = { pageOf: () => '00–00', totalPages: 0 }

function MeasureSection({ section }: { section: PacketSection }) {
  const frame = {
    band: section.band,
    title: section.title,
    index: 0,
    count: 1,
    pageLabel: 'Page 00 of 00',
    footerLabel: section.footer.label,
    printedAt: 'Thu, Oct 15, 10:00 PM',
  }
  const units = [...section.units, ...section.repeat]
  return (
    <>
      <PacketPage {...frame} mode="probe" bodyId={section.id} />
      <PacketPage {...frame} mode="stream">
        {units.map((unit) => (
          <div key={unit.id} data-packet-unit={unit.id}>
            <UnitView unit={unit} continued={false} compact={false} />
          </div>
        ))}
      </PacketPage>
      {section.preferOnePage ? (
        <PacketPage {...frame} mode="stream">
          {units.map((unit) => (
            <div key={unit.id} data-packet-unit-compact={unit.id}>
              <UnitView unit={unit} continued={false} compact />
            </div>
          ))}
        </PacketPage>
      ) : null}
    </>
  )
}

/** Every section once more, unpaginated: a probe page per section and each unit in a stream, for measuring. */
export function PacketMeasure({
  model,
  preliminaryLocksAt = null,
}: {
  model: PacketModel
  preliminaryLocksAt?: string | null
}) {
  return (
    <PrintHeaderNoticeContext.Provider
      value={preliminaryLocksAt ? <PrintPreliminaryLabel locksAtLabel={preliminaryLocksAt} /> : null}>
      <PacketRenderContext.Provider value={MEASURE_CONTEXT}>
        {model.sections.map((section) => (
          <MeasureSection key={section.id} section={section} />
        ))}
      </PacketRenderContext.Provider>
    </PrintHeaderNoticeContext.Provider>
  )
}
